import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../core/store';
import { LoadableStatus } from '../mobx-utils';
import { WeatherDay, WeatherHeader } from '@cfm-media/pwa-ui-components';
import { useWeatherForecast } from '../hooks/use-weather-forecast';
import { Loader, LoadSpinner } from '../components/loader';
import { ErrorPage } from '../components/error-page';
import { urls } from '../urls';
import { useTranslation } from '../core/utils/translations';
import { PageWrapper } from '../components/page-wrapper';
import { ContentWrapper } from '../components/content-wrapper';

export const Weather = observer(() => {
  const { BookingStore } = useStore();
  const [selectedDestinationIndex, setSelectedDestinationIndex] = useState(0);
  const forecast = useWeatherForecast();
  const { t } = useTranslation(['weather-page', 'error-pages']);

  if (BookingStore?.booking?.status !== LoadableStatus.done) {
    return <LoadSpinner />;
  }

  const geoLocations = BookingStore?.validGeoDataFromHotels;
  const travelsLength = geoLocations?.length;
  const weatherSateImage = '/public/assets/images/teasers/weather.jpg';

  if ([LoadableStatus.init, LoadableStatus.loading].includes(forecast.status)) {
    return <Loader />;
  }

  if (
    forecast?.status === LoadableStatus.error ||
    (forecast?.status === LoadableStatus.done &&
      (!forecast?.data || forecast?.data?.length === 0)) ||
    !geoLocations.length
  ) {
    return (
      <div className="h-full pt-[64px] pb-[85px] overflow-auto lg:pt-0 lg:pb-0 lg:pl-[300px]">
        <ErrorPage
          title={t('error-pages:weather-error-title-text')}
          body={t('error-pages:weather-error-body-text')}
          link={urls.home}
          buttonText={t('error-pages:weather-error-button-text')}
          icon={'icon-weather'}
        />
      </div>
    );
  }

  const currentDay = forecast?.data[selectedDestinationIndex][1];
  const next13Days = forecast?.data[selectedDestinationIndex].slice(1);

  const next = () => {
    selectedDestinationIndex >= travelsLength - 1
      ? setSelectedDestinationIndex(0)
      : setSelectedDestinationIndex(selectedDestinationIndex + 1);
  };

  const previous = () => {
    selectedDestinationIndex === 0
      ? setSelectedDestinationIndex(travelsLength - 1)
      : setSelectedDestinationIndex(selectedDestinationIndex - 1);
  };

  let currentCity = geoLocations[selectedDestinationIndex];

  return (
    <PageWrapper>
      <WeatherHeader
        city={currentCity?.name}
        currentDate={currentDay?.date}
        image={weatherSateImage}
        temperature={currentDay?.temperature?.max}
        stateIcon={currentDay?.stateIcon}
        onLeftArrowClick={previous}
        onRightArrowClick={next}
        isMultiDestination={geoLocations?.length > 1}
      />

      <ContentWrapper>
        {next13Days?.map((day) => (
          <WeatherDay
            date={day?.date}
            key={day?.date}
            temperatureUnit={'C'}
            maxTemperature={day?.temperature?.max + '°'}
            minTemperature={day?.temperature?.min + '°'}
            precipitation={day?.precipitation?.probability + '%'}
            windDirection={day?.wind?.direction}
            windSpeed={day?.wind?.max + ' km/h'}
            stateIcon={currentDay?.stateIcon}
            shortDaySyntax={true}
          />
        ))}
      </ContentWrapper>
    </PageWrapper>
  );
});
